import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Grid,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { AdminService } from '@/services/admin.service';
import { ControlType, Issuer, CloneControlTypeRequest } from '@/types';
import { CacheManager, CACHE_KEYS } from '@/utils/cache';

interface CloneControlTypeModalProps {
  open: boolean;
  controlType: ControlType | null;
  onClose: () => void;
  onSubmit: (id: string, data: CloneControlTypeRequest) => Promise<void>;
  isLoading?: boolean;
}

/**
 * Duplicate an existing kontrolltyp into a new version. The clone copies the
 * document requirements and form configuration but starts open to all farmers
 * (the allowlist is not copied) with today's date as activeFrom. The issuer can
 * be changed here, which is the primary reason cloning is offered as a distinct
 * flow from editing.
 */
const CloneControlTypeModal: React.FC<CloneControlTypeModalProps> = ({
  open,
  controlType,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const [issuers, setIssuers] = useState<Issuer[]>([]);
  const [isLoadingIssuers, setIsLoadingIssuers] = useState(false);
  const [newVersion, setNewVersion] = useState('');
  const [newName, setNewName] = useState('');
  const [issuerId, setIssuerId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load selectable issuers and seed the form from the source when opening.
  useEffect(() => {
    if (!open || !controlType) return;

    setNewVersion('');
    setNewName('');
    setIssuerId(controlType.issuerId);
    setErrors({});

    setIsLoadingIssuers(true);
    AdminService.getIssuers()
      .then((response) => {
        setIssuers(Array.isArray(response?.issuers) ? response.issuers : []);
      })
      .catch((error) => {
        console.error('Failed to load issuers:', error);
        setIssuers([]);
      })
      .finally(() => setIsLoadingIssuers(false));
  }, [open, controlType]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!newVersion.trim()) {
      newErrors['newVersion'] = 'Ny version är obligatorisk';
    }

    if (!issuerId) {
      newErrors['issuerId'] = 'Utfärdare är obligatorisk';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    setNewVersion('');
    setNewName('');
    setIssuerId('');
    setErrors({});
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!controlType || !validateForm()) {
      return;
    }

    try {
      await onSubmit(controlType.id, {
        newVersion: newVersion.trim(),
        ...(newName.trim() && { newName: newName.trim() }),
        issuerId,
      });

      // Clear the control types cache so the CreateControlPage will refresh
      CacheManager.remove(CACHE_KEYS.PREDEFINED_CONTROL_TYPES);

      handleClose();
    } catch (error) {
      console.error('Error cloning control type:', error);
    }
  };

  if (!controlType) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Typography variant="h5" component="div">
            Duplicera kontrolltyp
          </Typography>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Utgår från {controlType.name} • {controlType.issuer.displayName} • Version{' '}
            {controlType.version}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Ny version"
                  value={newVersion}
                  onChange={(e) => {
                    setNewVersion(e.target.value);
                    if (errors['newVersion']) {
                      setErrors((prev) => ({ ...prev, newVersion: '' }));
                    }
                  }}
                  error={!!errors['newVersion']}
                  helperText={errors['newVersion']}
                  required
                  placeholder="t.ex. 2026.2"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  select
                  label="Utfärdare"
                  value={issuerId}
                  onChange={(e) => {
                    setIssuerId(e.target.value);
                    if (errors['issuerId']) {
                      setErrors((prev) => ({ ...prev, issuerId: '' }));
                    }
                  }}
                  error={!!errors['issuerId']}
                  helperText={errors['issuerId'] || (isLoadingIssuers ? 'Laddar utfärdare...' : '')}
                  required
                  disabled={isLoadingIssuers}
                >
                  {isLoadingIssuers ? (
                    <MenuItem disabled>Laddar...</MenuItem>
                  ) : issuers.length === 0 ? (
                    <MenuItem disabled>Inga utfärdare tillgängliga</MenuItem>
                  ) : (
                    issuers.map((issuer) => (
                      <MenuItem key={issuer.id} value={issuer.id}>
                        {issuer.displayName} ({issuer.code})
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Nytt namn (valfritt)"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  helperText={`Lämna tomt för att behålla "${controlType.name}"`}
                  placeholder={controlType.name}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Dokumentkrav och formulärkonfiguration kopieras. Behörighetslistan
                    kopieras inte — den nya kontrolltypen börjar öppen för alla. Aktiv
                    från sätts till dagens datum.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleClose}
            disabled={isLoading}
          >
            Avbryt
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? 'Duplicerar...' : 'Duplicera'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CloneControlTypeModal;
